import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { login } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowRight, Shield, FileText, Brain, Briefcase } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await login({ email, password });
      loginUser(res.data.access_token, res.data.user);
      toast.success('Welcome back!');
      navigate('/hub');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-1 relative bg-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-navy-light" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 border border-white/20 rounded-full" />
          <div className="absolute top-40 right-20 w-48 h-48 border border-white/20 rounded-full" />
          <div className="absolute bottom-32 left-32 w-24 h-24 border border-white/20 rounded-full" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Legal AIDynamics" className="w-12 h-12 rounded-lg object-contain" />
            <div>
              <span className="font-heading text-2xl font-bold text-white">Legal AI</span>
              <span className="font-heading text-2xl font-bold text-bronze">Dynamics</span>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-8">
            <h2 className="font-heading text-3xl font-bold leading-tight">
              AI-Powered Tools for<br />Modern Litigation
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-bronze/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-bronze" />
                </div>
                <div>
                  <h3 className="font-semibold">Legal AIDynamics</h3>
                  <p className="text-white/70 text-sm">AI-powered exhibit identification for trial prep</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-5 h-5 text-white/70" />
                </div>
                <div>
                  <h3 className="font-semibold text-white/70">More Tools Coming</h3>
                  <p className="text-white/50 text-sm">Deposition Assistant, Timeline Builder & more</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white/70" />
                </div>
                <div>
                  <h3 className="font-semibold text-white/70">Enterprise Security</h3>
                  <p className="text-white/50 text-sm">AES-256 encryption & compliance ready</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quote */}
          <blockquote className="border-l-2 border-bronze pl-4">
            <p className="font-heading text-lg italic text-white/80">
              "The right exhibit at the right moment can change the entire trajectory of a case."
            </p>
          </blockquote>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/logo.png" alt="Legal AIDynamics" className="w-10 h-10 rounded-lg object-contain" />
            <div>
              <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
              <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
            </div>
          </div>

          <Card className="border-border shadow-lg">
            <CardHeader className="space-y-1 text-center pb-2">
              <CardTitle className="font-heading text-2xl">Welcome Back</CardTitle>
              <CardDescription className="font-body">
                Sign in to access your Legal AIDynamics tools
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="attorney@lawfirm.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="font-body h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-body">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="font-body h-11"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy-light font-body h-11 text-base"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm font-body text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/register" className="text-bronze hover:underline font-medium" data-testid="register-link">
                  Start your free trial
                </Link>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            By signing in, you agree to our{' '}
            <Link to="/terms-of-service" className="underline hover:text-foreground">
              Terms of Service
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
